import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import axios from "axios";
import { config } from "../assets/config/config";
import ThemedSuspense from "../components/ThemedSuspense";
import { notificationService, userServices } from "../services";
import { CometChat } from "@cometchat-pro/chat";
import io from "socket.io-client";


const apiUrl = config.api.url;

// create context
export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
  const [isLoaded, setLoaded] = useState(false);
  const [user, setUser] = useState(null);
  const [dependencyData, setDependency] = useState(null);
  const [signup, setSignup] = useState(null);
  const [isSignUpPopup, setIsSignUpPopup] = useState(false);
  const [isSignInPopup, setIsSignInPopup] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [isJobapply, setIsJobapply] = useState(false);
  const [isProfileFreezed, setIsProfileFreezed] = useState(false);
  const [signInTabs, setSignInTabs] = useState(null);
  const [isNewNotificationTiggered, setIsNewNotificationTiggered] = useState(false);
  const [refreshAvailableDates, setRefreshAvailableDates] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Use ref to track timeout so we can clear it on logout
  const refreshTimeoutRef = useRef(null);
  const initializingRef = useRef(false); // Track initialization state

  const GetSignUpDependency = async () => {
    return axios.get(`${apiUrl}/v1/get-all-masterData`).then((res) => {
      if (res) {
        setDependency(res.data);
      }
      return res;
    });
  };

// const refreshWebSocket = useCallback((user_id) => {
//     let socket;
//     socket = io(apiUrl);
//     socket.on("join", ({ payload }, callBack) => {
//       if (payload) {
//         return axios
//           .get(`${apiUrl}/v1/comet-chat/unread-count/${user_id}`)
//           .then((response) => {
//             if (response.data) {
//               setUnreadCount(response.data.totalUnreadCount);
//             } else {
//               setUnreadCount(0);
//             }
//           })
//           .catch((error) => {
//             setUnreadCount(0);

//             return error;
//           });
//       }
//     });
//   }, []);


  const refreshEditPage = useCallback((user_id) => {
    return axios
      .get(`${apiUrl}/v1/user/refresh-edit-profile/${user_id}`)
      .then((response) => {
        if (response.data.user_type == 1 || response.data.user_type == 2) {
          setUser(response.data);
        } else {
          setUser(null);
        }
      })
      .catch((error) => {
        setUser(null);
        return error;
      });
  }, []);

  // Helper function to check if access token is still valid
  const isAccessTokenValid = useCallback((token) => {
    if (!token || !token.expires) return false;

    // Check if token expires in more than 5 minutes (buffer time)
    const tokenExpires = moment(token.expires);
    const now = moment();
    const timeUntilExpiry = tokenExpires.diff(now, 'minutes');

    return timeUntilExpiry > 5; // Token is valid if it expires in more than 5 minutes
  }, []);

  // Helper function to check if we should attempt refresh
  const shouldAttemptRefresh = useCallback(() => {
    // Check if there are any cookies that suggest we might have a valid refresh token

    return document.cookie.includes('refreshToken') ||
      localStorage.getItem('hasValidUserSession') === 'true';
  }, []);

  const refreshTokens = useCallback(() => {
    console.log("Refreshing tokens...");
    return axios
      .post(`${apiUrl}/v1/auth/refresh-tokens`, {})
      .then((response) => {
        if (
          response.data.user?.user_type == 1 ||
          response.data.user?.user_type == 2
        ) {
          setAccessToken(response.data.token);
          setUser(response.data.user);
          setIsProfileFreezed(response.data.user?.isFreezed);
          setSignup(response.data.user);
          localStorage.setItem('hasValidUserSession', 'true');

          // Load dependencies after successful authentication
          GetSignUpDependency();
          setIsSignInPopup(false)
          return response;
        } else {
          // Invalid user type
          setUser(null);
          setSignup(null);
          setAccessToken(null);
          setIsSignInPopup(false)
          localStorage.removeItem('hasValidUserSession');
          throw new Error('Invalid user type');
        }
      })
      .catch((error) => {
        console.log("Token refresh failed:", error);
        setUser(null);
        setSignup(null);
        setAccessToken(null);
        localStorage.removeItem('hasValidUserSession');
        throw error;
      });
  }, []);

  const startSilentRefresh = useCallback(() => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (accessToken && isAccessTokenValid(accessToken)) {
      const tokenExpires = moment(accessToken.expires);
      // Refresh 5 minutes before expiry
      const timeUntilRefresh = tokenExpires.diff(moment().add(5, 'minutes'), 'milliseconds');

      if (timeUntilRefresh > 0) {
        refreshTimeoutRef.current = setTimeout(() => {
          // Only refresh if user is still logged in
          if (accessToken && user) {
            refreshTokens().catch((error) => {
              console.log("Silent refresh failed:", error);
              // Handle silent refresh failure - could redirect to login
            });
          }
        }, timeUntilRefresh);
      }
    }
  }, [accessToken, refreshTokens, user, isAccessTokenValid]);

  const checkNewNotification = (id) => {
    notificationService.getLastThreeNotificationByid(id).then((resp) => {
      if (resp?.data?.filter((each) => each?.status === false)?.length) {
        setIsNewNotificationTiggered(true);
      }
    });
  };

  const syncLogout = (event) => {
    if (event.key === 'logout') {
      setAccessToken(null);
      setUser(null);
      setIsProfileFreezed(false);
      localStorage.removeItem('hasValidUserSession');
      // Clear refresh timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    }
  };

  // Axios interceptor setup
  useEffect(() => {
    const interceptorId = axios.interceptors.request.use(
      (config) => {
        config.withCredentials = true;
        config.credentials = "include";
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken.token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, [accessToken]);

  // Initial authentication check - Fixed version
  useEffect(() => {
    const initializeAuth = async () => {
      // Prevent multiple initialization attempts
      if (initializingRef.current) return;
      initializingRef.current = true;

      try {
        // If we already have a valid access token, no need to refresh
        if (accessToken && isAccessTokenValid(accessToken) && user) {
          console.log("Already authenticated with valid token");
          return;
        }

        // Only attempt refresh if we think there might be a valid refresh token
        if (shouldAttemptRefresh()) {
          console.log("Attempting to refresh tokens...");
          await refreshTokens();
        } else {
          console.log("No refresh token available, skipping refresh");
        }
      } catch (error) {
        console.log('Auth initialization failed:', error);
        // Clear any stale data
        setUser(null);
        setAccessToken(null);
        localStorage.removeItem('hasValidUserSession');
      } finally {
        setLoaded(true);
        initializingRef.current = false;
      }
    };

    // Only initialize once when component mounts
    if (!isLoaded && !initializingRef.current) {
      initializeAuth();
    }
  }, [isLoaded]); // Remove other dependencies to prevent re-initialization

  // Start silent refresh when access token changes (but not during initial load)
  useEffect(() => {
    // Only start silent refresh if we're loaded and have a valid token
    if (isLoaded && accessToken && user) {
      startSilentRefresh();
    }

    // Cleanup timeout on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [accessToken, user, isLoaded, startSilentRefresh]);

  // Cross-tab logout sync
  useEffect(() => {
    window.addEventListener("storage", syncLogout);
    return function cleanup() {
      window.removeEventListener("storage", syncLogout);
    };
  }, []);

  const value = useMemo(() => {
    const register = (username, email, password) => {
      return axios
        .post(`${apiUrl}/v1/auth/register`, {
          name: username,
          email: email,
          password: password,
        })
        .then((response) => {
          setAccessToken(response.data.token);
          setUser(response.data.user);
          localStorage.setItem('hasValidUserSession', 'true');
          // Silent refresh will be started by the useEffect
        });
    };

    const login = (email, password) => {
      return axios
        .post(`${apiUrl}/v1/auth/login`, {
          email: email,
          password: password,
        })
        .then((response) => {
          window.localStorage.setItem("logout", null);
          setAccessToken(response.data.token);          
          localStorage.setItem('hasValidUserSession', 'true');
          setUser(response.data.user);
          // Silent refresh will be started by the useEffect
        });
    };

    const oAuthCreateAccount = (user) => {
      return axios.post(`${apiUrl}/v1/talenter`, user).then((response) => {
        setAccessToken(response.data.token);
        setUser(response.data.user);
        localStorage.setItem('hasValidUserSession', 'true');
        // Silent refresh will be started by the useEffect
      });
    };

    const updateUser = () => {
      return axios.get(`${apiUrl}/v1/user/${user.user_id}`).then((response) => {
        if (response) {
          setUser(response.data);
          setIsProfileFreezed(response.data?.isFreezed);
        }
      });
    };

    const githubAuth = () => {
      window.location.href = `${apiUrl}/v1/auth/github`;
    };

    const logout = async () => {
      // Clear refresh timeout immediately
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      localStorage.removeItem('hasValidUserSession');

      let payload = { user_id: user?.user_id, last_login_on: moment() };
      window.localStorage.setItem("logout", moment());
      
      if (user?.user_id) {
        userServices.userProfile(payload, user?.user_id).then((res) => { });
      }
      
      setAccessToken(null);
      setUser(null);
      setIsProfileFreezed(false);
      
      return axios
        .post(`${apiUrl}/v1/auth/logout`, {})
        .then((response) => {
          localStorage.removeItem("projectId");
          handleLogout();
          return response;
        })
        .catch((err) => {
          console.log("Logout API failed:", err);
        });
    };

    const handleLogout = () => {
      CometChat.logout().then(
        () => {
          console.log("User logged out successfully");
        },
        (error) => {
          console.log("Logout failed with error:", error);
        }
      );
    };

    const forgotPassword = (email) => {
      return axios.post(`${apiUrl}/v1/auth/forgot-password`, {
        email: email,
      });
    };

    const resetPassword = (password, resetToken) => {
      return axios.post(
        `${apiUrl}/v1/auth/reset-password?token=${resetToken}`,
        {
          password: password,
        }
      );
    };

    const verifyEmail = (emailVerificationToken) => {
      return axios.post(
        `${apiUrl}/v1/auth/verify-email?token=${emailVerificationToken}`,
        {}
      );
    };

    return {
      user,
      setUser,
      setSignup,
      signup,
      register,
      login,
      githubAuth,
      logout,
      forgotPassword,
      resetPassword,
      verifyEmail,
      oAuthCreateAccount,
      startSilentRefresh,
      setAccessToken,
      updateUser,
      isSignUpPopup,
      setIsSignUpPopup,
      setIsSignInPopup,
      isSignInPopup,
      GetSignUpDependency,
      dependencyData,
      setLoaded,
      isJobapply,
      setIsJobapply,
      setIsProfileFreezed,
      isProfileFreezed,
      setSignInTabs,
      signInTabs,
      isLoaded,
      setIsNewNotificationTiggered,
      isNewNotificationTiggered,
      checkNewNotification,
      accessToken,
      refreshTokens,
      refreshEditPage,
      refreshAvailableDates,
      setRefreshAvailableDates,
      unreadCount,
      setUnreadCount,
      // refreshWebSocket,

    };
  }, [
    user,
    refreshAvailableDates,
    startSilentRefresh,
    signup,
    isSignUpPopup,
    isSignInPopup,
    dependencyData,
    isProfileFreezed,
    signInTabs,
    isLoaded,
    isNewNotificationTiggered,
    unreadCount,
    setUnreadCount,
    accessToken, // Added accessToken to dependencies
  ]);

  // Show loading only for protected routes during initialization
  if (
    !isLoaded &&
    window.location.pathname !== "/talents" &&
    window.location.pathname !== "/"
  ) {
    return <ThemedSuspense />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};